import type { ReleaseConnection, ReleaseMapping } from "@packages/commons";
import { ensureParsedGraph } from "@imminently/decisively-core";
import type {
  AdapterGraphNode,
  AdapterTrigger,
  ReleaseWithAdapterCollections,
} from "./types";

export const findMappingById = (
  release: ReleaseWithAdapterCollections,
  id?: string,
): ReleaseMapping | undefined => release.mapping?.find((mapping) => mapping.id === id);

export const findMappingByName = (
  release: ReleaseWithAdapterCollections,
  name: string,
): ReleaseMapping | undefined => release.mapping?.find((mapping) => mapping.name === name);

export const findConnectionById = (
  release: ReleaseWithAdapterCollections,
  id?: string,
): ReleaseConnection | undefined => release.connections?.find((connection) => connection.id === id);

export const findConnectionByName = (
  release: ReleaseWithAdapterCollections,
  name: string,
): ReleaseConnection | undefined => release.connections?.find((connection) => connection.name === name);

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object";

const isGraphNode = (value: unknown): value is AdapterGraphNode =>
  isRecord(value) && typeof value.id === "string";

export const getGraphNodes = (release: ReleaseWithAdapterCollections): AdapterGraphNode[] => {
  if (!release.rule_graph) return [];
  try {
    const graph = typeof release.rule_graph === "string" ? JSON.parse(release.rule_graph) : release.rule_graph;
    const parsedGraph = ensureParsedGraph(graph as Parameters<typeof ensureParsedGraph>[0]);
    return parsedGraph.nodes().flatMap((nodeId) => {
      const node = parsedGraph.node(nodeId);
      return isGraphNode(node) ? [node] : [];
    });
  } catch {
    // A subresource mutation can return a release without its full rule graph.
    return [];
  }
};

export const findTrigger = (
  release: ReleaseWithAdapterCollections,
  attributeId?: string,
  title?: string,
  type?: string,
): { node: AdapterGraphNode; trigger: AdapterTrigger } | undefined => {
  if (!attributeId || !title) return undefined;

  const node = getGraphNodes(release).find((candidate) => candidate.id === attributeId);
  const trigger = node?.triggers?.find((candidate) => candidate.title === title && (!type || candidate.type === type));
  if (!node || !trigger) return undefined;

  return { node, trigger };
};

export const getNodeLabel = (node: AdapterGraphNode): string =>
  node.description || node.label || node.name || node.id;
